import React, { useEffect, useState } from 'react';
import { elevateSearch } from 'tools/elevateClient';
import { useTranslation } from 'tools/i18n';
import { Container, Grid, Typography } from '@material-ui/core';
import MetaTitle from '../../meta/title';
import { makeStyles } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import theme from '../../theme';
import RelevateGrid from "../../products/relevateGrid";
import { prepareFacets } from '@functions/elevateOps';
import ElevateListing from "./elevateListing";
import useSessionStore from '@services/stores/SessionStore';
import { base64Encode } from '@services/Utils';
import SearchResultContentItem from '@components/header/search/parts/SearchResultContentItem';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			paddingTop: 40,
			paddingBottom: 40
		},
		title: {
			textAlign: 'center',
			'& span': {
				color: '#999'
			}
		},
		subtitle: {
			marginBottom: 40,
			fontSize: 15,
			fontWeight: 300,
			textAlign: 'center'
		},
		searchPhraseAutocomplete: {
			margin: '10px 0 0',
			textAlign: 'center',
			fontStyle: 'italic',
			fontSize: 14,
			fontWeight: 300,
			color: theme.palette.primary.lowlight,
			'& span': {
				fontWeight: 'bold'
			}
		},
		container: {
			position: 'relative'
		},
        tabs: {
            display:'flex',
            justifyContent: 'center',
            margin: '1em 0 1.5em',
            '& > button': {
                background: 'transparent',
                padding: '10px 20px',
                border: 'none',
                fontSize: 16,
                fontFamily: 'Futura-pt',
                fontWeight: 500,
                color: '#888',
                '&.active': {
                    color: '#222',
                }
            },
        },
        tabIndicators: {
            fontSize: 14,
            background: '#f5f5f5',
            padding: '1px 7px',
            borderRadius: '4px',
            marginLeft: '3px'
        },
		sortingWrapper: {

		},
		filterGridWrapper: {
			display: 'flex',
			justifyContent: 'center',
			gap: 24,
			paddingTop: 20,
			['@media only screen and (max-width: 1524px)']: {
				justifyContent: 'flex-end',
			},
			[theme.breakpoints.down('lg')]: {
				paddingLeft: 0,
				paddingRight: 0
			},
			[theme.breakpoints.down('md')]: {
				flexDirection: 'column',
				paddingLeft: 0,
				paddingRight: 0
			},
			[theme.breakpoints.up('lg')]: {
				padding: '0',

				'@media screen and (min-width: 1296px)': {
					'&:after': {
						content: '""',
						width: 'calc(((100% - 1600px) / 2) - 24px)',
						maxWidth: 300,
						flexShrink: 0
					}
				}
			}
		},
		gridWrapper: {
			width: '75%',
			flexGrow: 1,
			margin: 0,
			[theme.breakpoints.down('md')]: {
				width: '100%'
			},
		},
		largeRelevatePadding: {
			['@media only screen and (min-width: 1600px)']: {
				padding: '0 20px',
			},
		},
		largeElevatePadding: {
			['@media only screen and (min-width: 1600px)']: {
				padding: '0 22px',
				maxWidth: '1600px',
				margin: 'auto',
			},
		},
        products__wrapper: {
            padding: '10px 20px',
            '&.inspiration': {
                padding: 10,
                margin: 0,
                width: '100%',
                [theme.breakpoints.down('md')]: {
                    padding: 5,
                },
            },
        },
        product: {
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            textDecoration: 'none',
            opacity: 0,
            backgroundColor: theme.palette.primary.background,
            '.inspiration  &': {
                flexDirection: 'column',
                margin: 'auto',
            },
            '&.focus': {
                opacity: 1,
            },
            '&:hover': {
                backgroundColor: theme.palette.primary.backgroundContrast
            },
            [theme.breakpoints.down('sm')]: {
                '&.focus': {
                    transitionDelay: '.5s'
                }
            }
        },
        product__image: {
            position: 'relative',
            width: 90,
            marginRight: 10,
            overflow: 'hidden',
            '&.inspiration': {
                marginRight: 0,
                width: '100%',
                aspectRatio: '16 / 9',
            },
            '& > div': {
                display: 'block !important'
            },
            '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                mixBlendMode: 'multiply',
                boxShadow: 'inset 0 0 90px 8px rgb(240, 240, 240, 0.75)'
            }
        },
        product__info: {
            display: 'flex',
            alignSelf: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '5px',
            '&.inspiration': {
                flexDirection: 'column',
                color: '#333',
                margin: '0 0 8px',
                fontSize: 16,
                textAlign: 'center',
                fontWeight: 400,
            },
        },
        product__name: {
            fontSize: 14,
            fontWeight: 300,
            color: theme.palette.primary.text,
            '.inspiration > &': {
                color: '#333',
                fontSize: 16,
                fontWeight: 400,
            },
        },
        product__prices: {
            display: 'flex'
        },
        product__price: {
            fontSize: 13,
            color: '#333',
            '&.special': {
                color: theme.palette.primary.special
            },
            '&.old': {
                marginLeft: 10,
                textDecoration: 'line-through',
                color: theme.palette.primary.disabled
            }
        },
        product__type: {
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.primary.text,
            textTransform: 'capitalize'
        },
        alert: {
            textAlign: "center"
        }

	}),
	{ name: 'SearchResults' }
);

function SearchResults({ query, router }) {
	const { t } = useTranslation(['general', 'attributes']);
	const classes = useStyles();
	const config = useSessionStore((state) => state.session.config);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState('products');
    const [showError, setShowError] = useState(false);
    const [showContentError, setShowContentError] = useState(false);

	// State
	const [elevateData, setElevateData] = useState({
		facets: [],
		productGroups: [],
		sort: { selected: 'RELEVANCE', options: [] },
		totalHits: 0
	});
    const [elevateContentData, setElevateContentData] = useState({
		items: [],
		sort: { selected: 'RELEVANCE', options: [] },
		totalHits: 0
	});
	const [params, setParams] = useState({ ...query, initial: true });

	// has Autocorrect
	const [autocorrect, setAutocorrect] = useState(false);

	// Secondary product list
	const [secondaryProducts, setSecondaryProducts] = useState([]);

	useEffect(() => {
		setParams({ ...query, initial: true });
	}, [query]);

	useEffect(async () => {
		let routerData = {
			pathname: router.asPath.split('?')[0],
			query: {
				...params
			},
			shallow: true
		};

		if (routerData.query.page === 1) {
			delete routerData.query.page;
		}

		delete routerData.query.initial;

		if (!params.initial) {
			router.push(routerData);
		}
	}, [params]);

	useEffect(async () => {
		let { q, origin, page, order, initial, ...filters } = query;

		if (q) {
			const isSoldOut = q.toLowerCase().indexOf('slutsåld') >= 0;

			const searchResults = await elevateSearch(
				q,
				order || 'RELEVANCE',
				prepareFacets(filters),
				page,
				config.elevateMarket,
				config.language.languageCode,
				isMobile ? 'mobile' : 'desktop',
				isSoldOut,
				origin === 'phrase' ? 'PHRASE_SUGGEST' : 'ORGANIC'
			);

			setElevateData(searchResults?.products?.primaryList);
			setElevateContentData(searchResults?.content?.primaryList);

			// Set products from secondary list
			if (searchResults?.products?.secondaryList?.productGroups) {
				let moreProducts = [];
				searchResults?.products?.secondaryList.productGroups.forEach(productSuggestions => {
					moreProducts.push(productSuggestions.products[0]);
				});

				setSecondaryProducts(moreProducts);
			} else {
				setSecondaryProducts([]);
			}

			if (searchResults?.products?.autoCorrect) {
				setParams({ ...params, q: searchResults?.products?.autoCorrect.q });
				setAutocorrect(searchResults?.products?.autoCorrect.q);
			} else {
				setAutocorrect(false);
			}
		}
	}, [router]);

    useEffect(() => {
        if (elevateData.totalHits <= 0 && elevateContentData.totalHits > 0) {
            setActiveTab('inspiration')
        }

        if (elevateData.totalHits > 0 && elevateContentData.totalHits <= 0) {
            setActiveTab('products')
        }

        const timer = setTimeout(() => {
            if (elevateData.totalHits <= 0) {
                setShowError(true);
            } else {
                setShowError(false);
            }

            if (elevateContentData.totalHits <= 0) {
                setShowContentError(true);
            } else {
                setShowContentError(false);
            }
        }, 1200);

        return () => clearTimeout(timer);
    }, [elevateData, elevateContentData]);

	return (
        <>
            <MetaTitle title={t('search-results-title', { query: query.q || '' })} />
            <div className={classes.wrapper}>
                <div className={classes.largeElevatePadding}>
                    <Typography className={classes.title} variant="h1">
                        {t('search-results-title', { query: '' })}
                        {autocorrect ?
                            <span>"{autocorrect}"</span>
                            :
                            <span>"{query.q}"</span>
                        }
                    </Typography>


                    {autocorrect && (
                        <Typography
                            className={classes.searchPhraseAutocomplete}
                            variant="body1"
                            dangerouslySetInnerHTML={{
                                __html: t('search-phrase-autocomplete', { originalPhrase: query.q }),
                            }}
                        />
                    )}


                    <Grid className={classes.tabs}>
                        <button
                            className={activeTab === 'products' ? 'active' : ''}
                            onClick={()=> setActiveTab('products')}
                        >
                            {t('search-results-tabs-products')} <span className={classes.tabIndicators}>{elevateData.totalHits}</span>
                        </button>

                        <button
                            className={activeTab === 'inspiration' ? 'active' : ''}
                            onClick={()=> setActiveTab('inspiration')}
                        >
                            {t('search-results-tabs-inspiration')}  <span className={classes.tabIndicators}>{elevateContentData.totalHits}</span>
                        </button>
                    </Grid>

                    {activeTab === 'products' &&
                        <>
                            <ElevateListing router={router} params={query} setParams={setParams} {...elevateData} />

                            {showError &&
                                <Grid xs={12} className={classes.noHitItem}>
                                    <Typography
                                        className={classes.alert}
                                        style={{ marginTop: 40, marginBottom: 20 }}
                                        variant="h3"
                                    >
                                        {t('search-result-no-hit')}
                                    </Typography>
                                </Grid>
                            }
                        </>
                    }

                    {activeTab === 'inspiration' &&
                        <>
                            <Grid xs={12} item>
                                <Grid className={[classes.products__wrapper, 'inspiration'].join(' ')} spacing={1} container>
                                    { elevateContentData.items.map(item =>
                                        <SearchResultContentItem key={item.key} props={{item, classes, source: 'page'}} />
                                    )}
                                </Grid>
                            </Grid>

                            {showContentError &&
                                <Grid xs={12} className={classes.noHitItem}>
                                    <Typography
                                        className={classes.alert}
                                        style={{ marginTop: 40, marginBottom: 20 }}
                                        variant="h3"
                                    >
                                        {t('search-result-content-no-hit')}
                                    </Typography>
                                </Grid>
                            }
                        </>
                    }
                </div>
                {secondaryProducts.length > 0 ? (
                    <>
                        <Container className={classes.largeRelevatePadding} disableGutters>
                            <Typography
                                className={classes.title}
                                style={{ marginTop: 40, marginBottom: 20 }}
                                variant="h2"
                            >
                                {t('search-related-products')}
                            </Typography>

                            <RelevateGrid
                                products={secondaryProducts}
                                paramsHash={base64Encode(JSON.stringify(params))}
                            />
                        </Container>
                    </>
                ) : (
                    ''
                )}
            </div>
        </>
    );
}

export default SearchResults;
